import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGet, usePost } from "../../Hooks/useFetch";
import usePayWay from "../../Hooks/usePayWay";
import PayWay from "../../Components/BoatRegistration/Payments/PayWay";
import { useForm } from "react-hook-form";
import DataLoader from "../../Components/DataLoader";
import SubmitLoader from "../../Components/SubmitLoader";
import AlertMessage from "../../Components/AlertMessage";
import { decryptText } from "../../Helpers/Helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import "./ApplyLicencePayment.css";

const ApplyLicencePayment = () => {
  const { mastRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const licenceOptions = location.state?.licenceOptions;

  // Redirect to options page if no options provided
  useEffect(() => {
    if (!licenceOptions) {
      navigate(`/licencepayment/${mastRef}`, { replace: true });
    }
  }, [licenceOptions, mastRef, navigate]);

  const [resKey, errKey, isLoadingKey, apiGetKey] = useGet();
  const [resFees, errFees, isLoadingFees, apiGetFees] = useGet();
  const [resPayment, errPayment, isLoadingPayment, apiPostPayment] = usePost();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState(null);

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: { payId: "" },
    mode: "onChange",
  });

  useEffect(() => {
    if (!licenceOptions) return;
    apiGetKey("/api/licence/PayWayKey");
    const params = new URLSearchParams({
      orderCard: licenceOptions.orderCard,
      visualAids: licenceOptions.visualAids,
      colourBlind: licenceOptions.colourBlind,
      eBilling: licenceOptions.eBilling,
    });
    apiGetFees(`/api/licence/LicenceFees/${mastRef}?${params.toString()}`);
  }, []);

  const payWayPublicKey = resKey
    ? decryptText(
        resKey,
        process.env.REACT_APP_Encrypt_Key,
        process.env.REACT_APP_Encrypt_IV,
      )
    : null;

  const {
    errFrame,
    errToken,
    tokenId,
    creditCard,
    btnDisable,
    frameDestroy,
    requestToken,
    createFrame,
    stateDestroy,
  } = usePayWay(payWayPublicKey);

  const [frameCreated, setFrameCreated] = useState(false);

  useEffect(() => {
    if (payWayPublicKey && !frameCreated) {
      setFrameCreated(true);
    }
  }, [payWayPublicKey]);

  const onSubmit = async (data, e) => {
    e.preventDefault();

    if (!tokenId) {
      requestToken();
      return;
    }

    await apiPostPayment(
      "/api/licence/ApplyLicencePayment",
      {
        MastRef: mastRef,
        CandStReceipt: data.payId,
        OrderCard: licenceOptions?.orderCard ?? true,
        VisualAidsRequired: licenceOptions?.visualAids ?? false,
        ColourBlindRequired: licenceOptions?.colourBlind ?? false,
        EBillingRequired: licenceOptions?.eBilling ?? true,
      },
      false,
    );
  };

  useEffect(() => {
    if (resPayment) {
      setPaymentSuccess(true);
      setReceiptNumber(resPayment);
    }
  }, [resPayment]);

  if (isLoadingKey || isLoadingFees) {
    return (
      <div className="dataLoader-container">
        <DataLoader isLoading={true} />
      </div>
    );
  }

  if (errKey || errFees) {
    return (
      <div className="apply-licence-container">
        <div className="licence-error-container">
          <AlertMessage message={errKey || errFees} />
          <button
            className="licence-back-btn"
            onClick={() =>
              navigate("/", { state: { from: location }, replace: true })
            }
          >
            <FontAwesomeIcon icon={faChevronLeft} /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="apply-licence-container">
        <div className="licence-success-container">
          <FontAwesomeIcon icon={faCheckCircle} size="3x" color="#1abc9c" />
          <h2>Payment Successful!</h2>
          <p>Your licence application payment has been processed.</p>
          {receiptNumber && (
            <p>
              Receipt Number: <strong>{receiptNumber}</strong>
            </p>
          )}
          <button
            className="licence-pay-btn"
            style={{ marginTop: "20px" }}
            onClick={() =>
              navigate("/", { state: { from: location }, replace: true })
            }
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {!isLoadingPayment ? (
        <div className="apply-licence-container">
          <div className="apply-licence-form">
            <h1>Finalise Licence - Payment</h1>
            <AlertMessage message={errPayment} />

            {resFees && (
              <>
                <div className="licence-invoice-details">
                  <h3>Applicant Details</h3>
                  <div className="invoice-detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{resFees.candName}</span>
                  </div>
                  {resFees.dateOfBirth && (
                    <div className="invoice-detail-row">
                      <span className="detail-label">Date of Birth:</span>
                      <span className="detail-value">
                        {new Date(resFees.dateOfBirth).toLocaleDateString(
                          "en-AU",
                        )}
                      </span>
                    </div>
                  )}
                  {resFees.age !== undefined && (
                    <div className="invoice-detail-row">
                      <span className="detail-label">Age:</span>
                      <span className="detail-value">{resFees.age}</span>
                    </div>
                  )}
                  {resFees.residenceAddress && (
                    <div className="invoice-detail-row">
                      <span className="detail-label">Residential Address:</span>
                      <span className="detail-value">
                        {resFees.residenceAddress}
                      </span>
                    </div>
                  )}
                  {resFees.mobile && (
                    <div className="invoice-detail-row">
                      <span className="detail-label">Mobile:</span>
                      <span className="detail-value">{resFees.mobile}</span>
                    </div>
                  )}
                  {resFees.email && (
                    <div className="invoice-detail-row">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{resFees.email}</span>
                    </div>
                  )}
                  {resFees.visualAids && (
                    <div className="invoice-detail-row">
                      <span className="detail-label">Visual Aids Required:</span>
                      <span className="detail-value">
                        {resFees.visualAids === "Y" ? "Yes" : "No"}
                      </span>
                    </div>
                  )}
                  {resFees.colourBlind && (
                    <div className="invoice-detail-row">
                      <span className="detail-label">Colour Blind:</span>
                      <span className="detail-value">
                        {resFees.colourBlind === "Y" ? "Yes" : "No"}
                      </span>
                    </div>
                  )}
                  {
                    <div className="invoice-detail-row">
                      <span className="detail-label">E-Billing:</span>
                      <span className="detail-value">
                        {licenceOptions.eBilling ? "Yes" : "No"}
                      </span>
                    </div>
                  }

                </div>

                <div className="licence-fee-summary">
                  <h3>Fee Summary</h3>
                  {resFees.hasMbl && (
                    <div className="fee-line-item">
                      <span>
                        MBL Licence Fee
                        {resFees.isExemption && (
                          <span className="fee-exemption"> (Exemption)</span>
                        )}
                      </span>
                      <span>${resFees.mblFee.toFixed(2)}</span>
                    </div>
                  )}
                  {resFees.hasPwc && (
                    <div className="fee-line-item">
                      <span>
                        PWC Licence Fee
                        {resFees.isExemption && (
                          <span className="fee-exemption"> (Exemption)</span>
                        )}
                      </span>
                      <span>${resFees.pwcFee.toFixed(2)}</span>
                    </div>
                  )}
                  {resFees.orderCard && (
                    <div className="fee-line-item">
                      <span>Card Fee</span>
                      <span>${resFees.cardFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="fee-total">
                    <span>Total</span>
                    <span>${resFees.totalFee.toFixed(2)}</span>
                  </div>
                </div>
              </>
            )}

            <div className="licence-pay-form">
              <form onSubmit={handleSubmit(onSubmit)}>
                {payWayPublicKey && (
                  <PayWay
                    errPayWayFrame={errFrame}
                    errPayWayToken={errToken}
                    tokenId={tokenId}
                    creditCard={creditCard}
                    createFrame={createFrame}
                    stateDestroy={stateDestroy}
                    setValue={setValue}
                  />
                )}
                <div className="licence-btn-group">
                  <button
                    type="button"
                    className="licence-back-btn"
                    onClick={() =>
                      navigate(`/licencepayment/${mastRef}`, {
                        state: { from: location },
                        replace: true,
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faChevronLeft} /> Back
                  </button>
                  <button
                    type="submit"
                    className="licence-pay-btn"
                    disabled={btnDisable}
                  >
                    {!tokenId
                      ? "Next"
                      : `Pay $${resFees?.totalFee?.toFixed(2) || "0.00"}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : (
        <SubmitLoader isLoading={isLoadingPayment} />
      )}
    </>
  );
};

export default ApplyLicencePayment;
